import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { isIOS } from "react-device-detect";
import { framePricesState, defaultFramePriceState, shippingPriceState } from "@atoms/priceCalc";
import { useDiscountConfig } from "@helpers/discounts";
import { useSetRecoilState } from "recoil";
import { giftCheckoutState } from "@atoms";
import { useSecondModal } from "@helpers/hooks/useSecondModal";
import {
  createGiftCardIntent,
  updateGiftCardIntent,
  getGiftCardStatus,
  getApplePayHandshake,
  loadTranzilaApplePayScript,
  logGiftFlowEvent,
} from "../../api/giftcard";
import { initializeSocket, subscribeToOrderStatus } from "../../api/orderStatusSocket";
import { getVisitorId, getAbVariant, trackAbExposure } from "../../api/visitTracking";
import Input from "@shared/components/Input2";
import "./giftcard.css";

const ASSET = "/assets/images/giftcard";
const CARD_BG = `${ASSET}/Gift-Card-Skeleton.png`;
const CARD_LOGO = `${ASSET}/CardLogo.png`;
const IMG_ICON = `${ASSET}/Images%20Icon.png`;
const SMILE = `${ASSET}/Smile.svg`;
const NAME_LOGO = `${ASSET}/nameLogo.svg`;
// 3D clay benefit icons (generated to match the mascot finish)
const IC3D = `${ASSET}/icons`;
// mask-image must be set inline (public path) so css-loader doesn't try to bundle it.
const maskOf = (url) => ({ WebkitMaskImage: `url(${url})`, maskImage: `url(${url})` });

// Default bundles shown as cards (frame counts) — one per visual tier so the
// carousel showcases every finish. The popular 12 LEADS the list (and is the
// default): 12 green · 5 rose · 8 emerald · 16 pearl · 24 onyx gold · 36 paper.
const BUNDLES = [12, 5, 8, 16, 24, 36];
const BUNDLE_LABELS = { 12: "פופולארי", 24: "משתלם" };
// Card finish by frame count (tiers):
// 1–7 rose (festive) · 8–11 emerald monogram · 12–15 green (full logo) ·
// 16–19 pearl holo · 20–29 onyx gold monogram · 30+ paper white.
const themeFor = (n) => {
  if (n >= 30) return "gc-paper";
  if (n >= 20) return "gc-onyx";
  if (n >= 16) return "gc-pearl";
  if (n >= 12) return "gc-green";
  if (n >= 8) return "gc-emerald";
  return "gc-rose"; // 1–7
};

/**
 * Real storefront discount % for a frame count (mirrors ReviewImages
 * checkValidCoupon): highest tier with imageCount <= frames, honouring
 * enableDefault / fallbackToLower.
 */
function selectDiscount(frames, cfg) {
  const keys = Object.keys(cfg || {}).map(Number).filter((n) => !isNaN(n)).sort((a, b) => b - a);
  const tierKey = keys.find((k) => k <= frames);
  if (tierKey === undefined) return 0;
  const tier = cfg[tierKey];
  if (!tier.enableDefault) {
    if (tier.fallbackToLower) {
      const lower = keys.filter((k) => k < tierKey && cfg[k].enableDefault).sort((a, b) => b - a)[0];
      if (lower !== undefined) return cfg[lower].discount;
    }
    return 0;
  }
  return tier.discount;
}

/* ── The gift-card visual (brand smile + per-tier finish, monogram/festive,
   tinted wordmark or full logo) ── */
// ALL cards use BAKED images (the CSS composition's rem-sized logo/badge don't
// scale down). Custom amounts resolve to their theme range's representative
// bundle card, so the whole range shows the same baked finish.
const BAKED_CARDS = { 5: 1, 8: 1, 12: 1, 16: 1, 24: 1, 36: 1 };
const THEME_BUNDLE = { "gc-rose": 5, "gc-emerald": 8, "gc-green": 12, "gc-pearl": 16, "gc-onyx": 24, "gc-paper": 36 };
const GiftCardVisual = ({ frames, label, hideBadge }) => {
  if (!label && !hideBadge) {
    const rep = BAKED_CARDS[frames] ? frames : THEME_BUNDLE[themeFor(frames)];
    const th = themeFor(frames);
    return (
      <span className={`gc-card-wrap ${th}`}>
        {/* width/height reserve the box before load — no layout jump (CLS) */}
        <img className="gc-card-img" src={`${ASSET}/card-tier-${rep}.webp`} width={1140} height={705} alt="" draggable="false" />
        {/* LIVE amount (number only — the icon is baked into the card art).
            SVG viewBox keeps it proportional; the number stays truthful for
            any custom amount. Positioned to sit just left of the baked icon. */}
        <svg className={`gc-card-live-badge ${th}`} viewBox="0 0 60 26" aria-hidden="true">
          <text x="58" y="19.5" textAnchor="end" className="gc-lb-t"><tspan className="gc-lb-s">x</tspan>{frames}</text>
        </svg>
      </span>
    );
  }
  const theme = themeFor(frames);
  const hasMono = theme === "gc-emerald" || theme === "gc-onyx";
  const isFestive = theme === "gc-rose";
  const isFullLogo = theme === "gc-green";
  const hasHalo = theme !== "gc-paper";
  return (
    <div className={`gc-card ${theme}`}>
      <img className="gc-card-bg" src={CARD_BG} alt="" />
      {hasMono && <span className="gc-mono" style={maskOf(SMILE)} />}
      {hasHalo && <span className="gc-halo" />}
      {isFestive && (
        <span className="gc-fx">
          <span className="gc-heart" style={{ top: "22%", left: "30%" }}>
            <svg viewBox="0 0 24 24" fill="#d9577f"><path d="M12 21s-7.5-4.7-10-9.3C.4 8.6 2 5 5.4 5c2 0 3.4 1.2 4.6 2.8C11.2 6.2 12.6 5 14.6 5 18 5 19.6 8.6 22 11.7 19.5 16.3 12 21 12 21z" /></svg>
          </span>
          <i className="d" style={{ top: "70%", left: "22%", background: "#d98aa0" }} />
          <i className="s" style={{ top: "24%", left: "72%", background: "#fff", transform: "rotate(-22deg)" }} />
          <i className="d" style={{ top: "38%", left: "88%", background: "#e8b48a" }} />
          <i className="s" style={{ top: "80%", left: "60%", background: "#ffd2e1", transform: "rotate(18deg)" }} />
        </span>
      )}
      <span className="gc-smile" style={maskOf(SMILE)} />
      <span className="gc-ovl" />
      <span className="gc-grain" />
      <span className="gc-sheen" />
      {label && <span className="gc-card-label">{label}</span>}
      {!hideBadge && (
        <div className="gc-card-badge">
          <span className="gc-x"><small>x</small>{frames}</span>
          <img src={IMG_ICON} alt="" />
        </div>
      )}
      <div className="gc-card-logo">
        {isFullLogo
          ? <img className="gc-fulllogo" src={CARD_LOGO} alt="Blends" />
          : <span className="gc-namelogo" style={maskOf(NAME_LOGO)} />}
      </div>
    </div>
  );
};

/* ── Benefit copy block (below the card): V4 chips on mobile, V1 grid on desktop ── */
const I = {
  frame: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M21 16l-5-5L8 19" /><circle cx="8.5" cy="10.5" r="1.5" /></svg>,
  home: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11l9-8 9 8" /><path d="M5 10v10h14V10" /><path d="M9 21v-6h6v6" /></svg>,
  heart: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1a5.5 5.5 0 1 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8z" /></svg>,
  bolt: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h7l-1 8 10-12h-7z" /></svg>,
  truck: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="5" width="14" height="12" rx="2" /><path d="M15 9h4l3 3v3h-3" /><circle cx="6" cy="18" r="2" /><circle cx="18" cy="18" r="2" /></svg>,
  phone: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="7" y="2" width="10" height="20" rx="2" /><path d="M11 18h2" /></svg>,
  mail: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 7l-10 6L2 7" /></svg>,
  bubble: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.5 8.5 0 0 1-8.5 8.5c-1.55 0-3-.41-4.26-1.13L3.5 20l1.18-4.5A8.5 8.5 0 1 1 21 11.5z" /><path d="M8.5 10.5h7M8.5 13.5h4.5" /></svg>,
  cal: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M3 9h18M8 2v4M16 2v4" /></svg>,
  split: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7h13l5 5-5 5H3z" /><path d="M3 7v10" /></svg>,
  star: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l2.4 4.9 5.4.8-3.9 3.8.9 5.4L12 19l-4.8 2.5.9-5.4L4.2 12.3l5.4-.8z" /></svg>,
  user: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>,
  tel: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" /></svg>,
};
const GiftBenefitsGrid = () => (
  <div className="gc-benefits-grid">
    <div className="gc-benefit">{I.heart}<div><div className="bt">הם בוחרים - אתם נותנים את הרגש</div><div className="bs">בלי להתלבט אילו תמונות</div></div></div>
    <div className="gc-benefit">{I.frame}<div><div className="bt">מתנה שתלויה על הקיר</div><div className="bs">לא נשכחת במגירה - רגעים יקרים בבית כל יום</div></div></div>
    <div className="gc-benefit">{I.truck}<div><div className="bt">מגיע ממוסגר עד הבית</div><div className="bs">מוכן לתלייה, כולל אריזת מתנה · משלוח חינם</div></div></div>
    <div className="gc-benefit">{I.home}<div><div className="bt">בית מלא באנשים שאוהבים</div><div className="bs">קיר גלריה אישי שמספר את הסיפור שלכם</div></div></div>
    <div className="gc-benefit">{I.bolt}<div><div className="bt">רכישה ב‑30 שניות</div><div className="bs">נשלח מיד ב‑SMS - מתנה של הרגע האחרון? פתרתם</div></div></div>
    <div className="gc-benefit">{I.phone}<div><div className="bt">פשוט למימוש מהנייד</div><div className="bs">תקף לשנה · מימוש חלקי</div></div></div>
  </div>
);

/* ── Tier environment clip — grained B&W treatment lives in CSS.
   Owner-approved WIDE finals (Kling pipeline: reversed pan → angled → zoom,
   ~14s, native 2.5:1) roll out per tier via WIDE_FINAL; tiers without one
   still play the legacy square boomerang loops. Tier switches blur-crossfade
   over the still-playing previous clip. Custom amounts play the nearest
   bundle's clip. ── */
const VIDEO_BASE = "/assets/videos/giftcard";
// Cloudinary delivery (owner: webm for loading speed): eager-derived q_auto
// webm (VP9) first, Cloudinary mp4 for browsers without webm (old iOS), local
// mp4 as emergency last resort. Masters live in assets; upload via
// server/scripts/uploadGiftcardVideos.js (eager pre-transcode - lazy derive
// truncates first plays).
const CDN_VIDEO = "https://res.cloudinary.com/dzb3a5brn/video/upload/q_auto/blends/giftcard";
// tiers whose merged wide final passed owner approval
const WIDE_FINAL = { 5: true, 8: true, 12: true, 16: true, 24: true, 36: true };
// scene baked into each legacy square loop (poster must match)
const SCENE_PICK = { 5: "", 8: "b", 12: "", 16: "b", 24: "b", 36: "" };
// per-tier vertical crop bias (defaults to the stylesheet's 30%):
// higher % = window sits lower in the scene. 24 biased down, 8 biased up.
const CROP_Y = { 8: "18%", 24: "42%" };
const srcFor = (t) => {
  const name = WIDE_FINAL[t] ? `gift-env-${t}-wide-hf-final` : `gift-env-${t}-loop`;
  const poster = WIDE_FINAL[t] ? `gift-env-${t}-wide.webp` : `gift-env-${t}${SCENE_PICK[t]}.webp`;
  return {
    webm: `${CDN_VIDEO}/${name}.webm`,
    mp4: `${CDN_VIDEO}/${name}.mp4`,
    fallback: `${VIDEO_BASE}/${name}.mp4`,
    poster: `${VIDEO_BASE}/${poster}`,
  };
};
const GiftEnvVideo = ({ frames }) => {
  const n = BUNDLES.reduce((a, b) => (Math.abs(b - frames) < Math.abs(a - frames) ? b : a));
  const [layers, setLayers] = useState([n]); // [current] or [previous, incoming]
  useEffect(() => {
    setLayers((L) => (L[L.length - 1] === n ? L : [L[L.length - 1], n]));
  }, [n]);
  const settle = () => setLayers((L) => [L[L.length - 1]]);
  return (
    <div className="gc-envid">
      {layers.map((t, i) => {
        const { webm, mp4, fallback, poster } = srcFor(t);
        return (
          <video
            key={t}
            className={i > 0 ? "gc-vin" : ""}
            autoPlay muted loop playsInline preload="auto"
            poster={poster}
            style={CROP_Y[t] ? { objectPosition: `50% ${CROP_Y[t]}` } : undefined}
            onAnimationEnd={i > 0 ? settle : undefined}
          >
            <source src={webm} type="video/webm" />
            <source src={mp4} type="video/mp4" />
            <source src={fallback} type="video/mp4" />
          </video>
        );
      })}
      <span className="gc-envid-grain" aria-hidden="true" />
    </div>
  );
};

/* ── Rotating benefit line under the choose-screen CTA: one pinned slot, the
   current line scrolls up and out while the next scrolls in from below (the
   flow's ease curve), every 4.5s. Owner-approved lines only. ── */
const GcNoteTicker = ({ lines, interval = 4500 }) => {
  const [cur, setCur] = useState(0);
  const prevRef = useRef(-1);
  useEffect(() => {
    if (lines.length < 2) return;
    const t = setInterval(
      () => setCur((c) => { prevRef.current = c; return (c + 1) % lines.length; }),
      interval
    );
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines.length, interval]);
  return (
    <div className="gc-note gc-note-ticker" aria-live="polite">
      {lines.map((l, i) => (
        <span key={i} className={`gc-tick-line${i === cur ? " on" : i === prevRef.current ? " out" : ""}`}>{l}</span>
      ))}
    </div>
  );
};

/* The canonical Blends V tick (ring-less inline variant) — done-state pills. */
const VTick = (
  <svg className="gc-pill-tick" viewBox="0 0 52 52" aria-hidden="true">
    <path d="M15 27l7.5 7.5L37 19" />
  </svg>
);

/* Quick greeting presets — a pill fills the textarea, the user can edit after. */
const GREETING_PRESETS = [
  { label: "🎂 יום הולדת", text: "מזל טוב ליום ההולדת! שתמיד יהיו לך רגעים יפים שמגיעים עד הקיר 🎉" },
  { label: "👶 לידה", text: "מזל טוב על ההרחבה המתוקה! שהבית יתמלא ברגעים קטנים וגדולים על הקיר 💚" },
  { label: "💍 יום נישואין", text: "מזל טוב ליום הנישואין! שהאהבה שלכם תמשיך למלא את הקירות ברגעים יפים ❤️" },
  { label: "🏠 בית חדש", text: "מזל טוב על הבית החדש! שכל קיר יתמלא בזיכרונות שאוהבים 🏠" },
  { label: "💒 חתונה", text: "מזל טוב לחתונה! שהבית החדש שלכם יתמלא ברגעים של אהבה על כל קיר 💍" },
  { label: "🎓 סיום לימודים", text: "מזל טוב על הסיום! גאים בך - שהרגעים הגדולים ימשיכו להצטבר על הקיר 🎓" },
  { label: "🙏 תודה", text: "תודה ענקית מכל הלב! קצת רגעים יפים שיזכירו כמה שאתם מיוחדים 💚" },
  { label: "✨ חג שמח", text: "חג שמח! שהחג ימלא את הבית ברגעים חמים שנשארים על הקיר ✨" },
  { label: "💚 סתם מפנקים", text: "סתם כי בא לנו לפנק! שתבחרו את הרגעים הכי יפים ותתלו אותם על הקיר 💚" },
];

/**
 * GiftCard — shared stepped purchase flow.
 * mode="page" (menu) | "modal" (onboarding, adds the self-add link).
 */
const GiftCard = ({ mode = "page", onSelfAdd, onClose }) => {
  const navigate = useNavigate();
  const framePrices = useRecoilValue(framePricesState);
  const defaultFramePrice = useRecoilValue(defaultFramePriceState);
  const framePrice = (framePrices && framePrices.classic) || defaultFramePrice || 49;
  const shippingPrice = useRecoilValue(shippingPriceState);
  const { discountConfig } = useDiscountConfig();

  // 0 = opening/intro · 1 = choose · 2 = details. The flow opens on choose —
  // the intro is off for now (its screen stays below; start at 0 to restore).
  const [step, setStep] = useState(1);
  const [selectedIdx, setSelectedIdx] = useState(0); // default 12 (popular) — leads the list
  const [isCustom, setIsCustom] = useState(false);
  const [customFrames, setCustomFrames] = useState(8);
  // ±1 stepping with a ghost-click guard: any second activation within 80ms
  // (native spinner overlap, synthetic double-fire) is one user intent — the
  // custom stepper is core, a skipped value (e.g. 20→18 over the 19 tier
  // boundary) reads as broken.
  const stepAtRef = useRef(0);
  const stepCustom = (d) => {
    const now = Date.now();
    if (now - stepAtRef.current < 80) return;
    stepAtRef.current = now;
    setCustomFrames((f) => Math.max(1, Math.min(50, f + d)));
  };

  const frames = isCustom ? customFrames : BUNDLES[selectedIdx];

  const [recipientName, setRecipientName] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [recipientEmail, setRecipientEmail] = useState(""); // optional — congrats email to the recipient
  // Giver details prefill from the normal-flow onboarding, when it ran in
  // this browser (fullName/userPhone/userEmail are its localStorage keys).
  const ls = (k) => { try { return localStorage.getItem(k) || ""; } catch (e) { return ""; } };
  const [senderName, setSenderName] = useState(() => ls("fullName"));
  const [buyerPhone, setBuyerPhone] = useState(() => ls("userPhone"));
  const [buyerEmail, setBuyerEmail] = useState(() => ls("userEmail"));
  const [greeting, setGreeting] = useState("");
  const [withGreeting, setWithGreeting] = useState(false);
  // The card always goes to the buyer (SMS + email). Opting in adds the
  // recipient as an extra delivery target.
  const [withRecipient, setWithRecipient] = useState(false);
  const [sendWhen, setSendWhen] = useState("now");
  const [sendDate, setSendDate] = useState("");

  // Payment now runs inside the payment-methods popup; the legacy "paying"
  // phase screen stays only as a dead-branch fallback.
  const [phase] = useState("form");
  const [error, setError] = useState("");
  // Per-field validation errors — invalid fields get the onboarding errored
  // look (red border/tint) via Input2's error prop.
  const [fieldErrors, setFieldErrors] = useState({});
  const pollRef = useRef(null);
  const carRef = useRef(null);
  const tickRef = useRef(false);

  // Mobile details screen (step 2): the giver form sits inline (desktop
  // pattern — labelled Input2 fields with required marks); the card shrinks a
  // step so the whole screen fits one viewport.
  const [isMobile, setIsMobile] = useState(() => {
    try { return window.matchMedia("(max-width: 767px)").matches; } catch (e) { return false; }
  });
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const on = () => setIsMobile(mq.matches);
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on); };
  }, []);

  // Funnel: one "view" per mount (page or onboarding modal).
  useEffect(() => {
    logGiftFlowEvent("view", {}, mode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A/B (desktop only): where the pay-screen total sits — above the card
  // visual ('card') vs above the CTA button ('cta'). Sticky 50/50 by
  // visitorId; stats land in the admin CRO panel (exposures + paid cards).
  const totalVariant = getAbVariant("gcTotalPlacement", ["card", "cta"]);
  // Exposure: count the visitor into the experiment only when the desktop
  // pay screen actually renders. Deduped per visitor/day server-side.
  useEffect(() => {
    if (step === 2 && !isMobile) trackAbExposure("gcTotalPlacement", totalVariant);
  }, [step, isMobile]); // totalVariant is sticky per visitor — not a dep
  // Greeting / recipient editors live in a modal (mobile: bottom sheet,
  // desktop: centered dialog): null | "greeting" | "recipient".
  const [extrasModal, setExtrasModal] = useState(null);

  // ── Payment: the CTA opens the payment-methods popup directly (no cart
  // drawer) — every method charges the server-priced gift amount. ──
  const [payBusy, setPayBusy] = useState(false);
  const setGiftCheckout = useSetRecoilState(giftCheckoutState);
  const secondModal = useSecondModal();
  // Native Apple Pay is only offered where the Apple Pay sheet can actually
  // open (iOS Safari / SFSafariViewController expose ApplePaySession).
  const canApplePay = isIOS && typeof window !== "undefined" && !!window.ApplePaySession;
  // Last created PENDING intent — reused (with a details refresh) as long as the
  // frame count hasn't changed, so retries don't mint orphans.
  const intentRef = useRef(null);

  // Leaving the page: drop gift-checkout mode + the GC- order id so the normal
  // photo flow never inherits them.
  useEffect(() => () => {
    setGiftCheckout(null);
    const oid = localStorage.getItem("orderId");
    if (oid && oid.startsWith("GC-")) localStorage.removeItem("orderId");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Choose + details screens (mobile): the black top bar makes way for the
  // clip — the menu/chat buttons float over it as glass chips (styling scoped
  // by this body class in giftcard.css, mobile-only). Both merged steps keep
  // it so the shell never relayouts on the 2→3 morph (video/header stay put;
  // only the card eases down a size step to make room for the form).
  useEffect(() => {
    if (step === 1 || step === 2) {
      document.body.classList.add("gc-glass-top");
      return () => document.body.classList.remove("gc-glass-top");
    }
  }, [step]);

  // Pay entrance done → gc-settled drops the finished animations (see the
  // giftcard.css block): fill-mode:both keeps WebKit compositing the rows
  // after the stagger, and the floating field labels focused later rasterize
  // into that stale motion layer — swollen/blurry text on phones. The last
  // delay+duration ends at ~0.9s.
  const [motionSettled, setMotionSettled] = useState(false);
  useEffect(() => {
    if (step !== 2) { setMotionSettled(false); return; }
    const t = setTimeout(() => setMotionSettled(true), 950);
    return () => clearTimeout(t);
  }, [step]);

  useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);

  // The app-shell accessibility widget keeps an off-screen menu that overflows
  // <body> horizontally and shifts it left — which clips the centred desktop
  // card. Clip body/html horizontal overflow while this page is mounted.
  useEffect(() => {
    const b = document.body.style.overflowX;
    const h = document.documentElement.style.overflowX;
    document.body.style.overflowX = "hidden";
    document.documentElement.style.overflowX = "hidden";
    // .page-content-wrapper has will-change:transform → it becomes the containing
    // block and (together with the accessibility-widget's off-screen overflow)
    // shifts the centred desktop card left. Neutralise it while mounted.
    const wrap = document.querySelector(".page-content-wrapper");
    const prevWill = wrap ? wrap.style.willChange : "";
    if (wrap) wrap.style.willChange = "auto";
    return () => {
      document.body.style.overflowX = b;
      document.documentElement.style.overflowX = h;
      if (wrap) wrap.style.willChange = prevWill;
    };
  }, []);

  /* ── Carousel motion: ONE authority. Native scroll-snap is OFF for this
     carousel (see giftcard.css) — mandatory re-snapping was the return
     jiggle: entering pay suspends snap (perfect morph), returning re-armed
     it against a position the grow-back morph had just moved, so WebKit
     re-snapped with a visible tilt and often landed off-centre. Instead:
     · glideTo(i)      — self-correcting rAF tween; recomputes the target's
                         LIVE centre every frame, so it stays true under
                         concurrent flex-basis changes and lands dead-centre
     · the step morph  — per-frame instant hold (unchanged feel, 2→3 is the
                         reference), now with nothing to re-snap after
     · swipes          — momentum runs natively; when scroll events go quiet
                         (and no finger is down) a short glide soft-snaps to
                         the nearest card. ── */
  const glideUntilRef = useRef(0);     // a smooth glide is in flight until this timestamp
  const touchOnRef = useRef(false);    // finger on the carousel

  // Delta-based measuring is RTL-safe (scrollIntoView would also scroll the
  // page's VERTICAL scroller — the window "jumps" on tier change).
  const slotDelta = (i) => {
    const el = carRef.current;
    const s = slots()[i];
    if (!el || !s) return 0;
    const sr = s.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    return sr.left + sr.width / 2 - (er.left + el.clientWidth / 2);
  };

  // Nearest card to the carousel's centre (getBoundingClientRect = RTL-safe).
  const nearestIdx = () => {
    const el = carRef.current;
    if (!el) return 0;
    const mid = el.getBoundingClientRect().left + el.clientWidth / 2;
    let best = 0, bd = Infinity;
    slots().forEach((c, i) => {
      const r = c.getBoundingClientRect();
      const d = Math.abs(r.left + r.width / 2 - mid);
      if (d < bd) { bd = d; best = i; }
    });
    return best;
  };

  const glideInFlight = () => performance.now() < glideUntilRef.current;
  const cancelGlideWindow = () => { glideUntilRef.current = 0; };

  /* HARD RULE, learned on-device: iOS RTL scroll-snap can NEITHER be toggled
     NOR fought. Toggling snap-type re-snaps against a stale offset table
     (custom-exit landed on the far end); any corrector writing against the
     armed engine reaches a permanent oscillation with it (the filmed
     self-jiggle in pay). So: snap stays armed FOREVER, and a programmatic
     move is exactly ONE snap-compatible scroll command — smooth for glides,
     instant for repositions — that the snap engine then finalises ITSELF.
     Nothing else ever writes to this scroller. */
  const glideTo = (i, instant) => {
    const el = carRef.current;
    if (!el) return;
    const d = slotDelta(i);
    if (Math.abs(d) < 1) return;
    // ABSOLUTE target: relative scrollBy calls issued in the same tick
    // compound in Chromium (an instant reposition + a smooth glide landed a
    // full card past the target); scrollTo cannot compound.
    const left = el.scrollLeft + d;
    if (instant) {
      el.scrollTo({ left });
      return;
    }
    glideUntilRef.current = performance.now() + 900;
    el.scrollTo({ left, behavior: "smooth" });
  };

  // Step changes. Fresh entry to the choose screen (0→1): one instant centre,
  // no motion — the screen just opens composed. The 1↔2 pay morph is a FLIP:
  // animating flex-basis and chasing it with scroll corrections can NEVER be
  // smooth on iOS (programmatic scroll writes apply a frame behind layout —
  // the filmed wobble). Instead the slot sizes SNAP while the neighbours are
  // invisible (faded), the carousel recentres in the same hidden frame, and
  // the visible shrink/grow of the chosen card is a pure compositor
  // transform — no layout animation, no scroll writes, nothing to chase.
  const prevStepRef = useRef(0);
  // The pay morph's card resize runs as a WAAPI px->px animation: both
  // endpoints are MEASURED (w0 snapshotted just before the step flips, w1
  // from the final layout), so no CSS unit interpolation is involved.
  const wrapW0Ref = useRef(0);
  const wrapAnimRef = useRef(null);
  const goStep = (n) => {
    if (n === 2) logGiftFlowEvent("continue_to_details", { frames, custom: isCustom });
    const slot = slots()[selectedIdx];
    const wrap = slot && slot.querySelector(".gc-card-wrap");
    wrapW0Ref.current = wrap ? wrap.getBoundingClientRect().width : 0;
    setStep(n);
  };
  useLayoutEffect(() => {
    const el = carRef.current;
    const fromStep = prevStepRef.current;
    prevStepRef.current = step;
    if (!el) return;
    const enterPay = fromStep === 1 && step === 2;
    const leavePay = fromStep === 2 && step === 1;
    if (!enterPay && !leavePay) {
      requestAnimationFrame(() => glideTo(selectedIdx, true));
      return;
    }
    // THE MORPH WRITES NOTHING. Slots, spacers and scrollLeft are identical
    // in both steps — the card eases its own width inside its fixed slot
    // (CSS), neighbours fade in place, snap keeps holding the same card.
    // Only on the way back: if something external moved the scroller while
    // pay was open (rare), one smooth glide re-syncs to the chosen card.
    cancelGlideWindow();
    const slot = slots()[selectedIdx];
    const wrap = slot && slot.querySelector(".gc-card-wrap");
    if (wrap) {
      const w0 = wrapW0Ref.current;
      const w1 = wrap.getBoundingClientRect().width; // final layout (no CSS width transition exists)
      if (wrapAnimRef.current) wrapAnimRef.current.cancel();
      if (w0 > 1 && w1 > 1 && Math.abs(w0 - w1) > 1) {
        wrapAnimRef.current = wrap.animate(
          [{ width: w0 + "px" }, { width: w1 + "px" }],
          // entering waits for the neighbour fade; leaving grows immediately
          { duration: 450, delay: enterPay ? 150 : 0, easing: "cubic-bezier(0.22, 1, 0.36, 1)", fill: "backwards" }
        );
      }
    }
    wrapW0Ref.current = 0;
    if (leavePay) {
      const idx = selectedIdx;
      requestAnimationFrame(() => {
        if (Math.abs(slotDelta(idx)) > 8) glideTo(idx);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Custom mode needs NO carousel choreography any more: the six slots stay
  // mounted, the neighbours fade via CSS (like pay) and the selected card
  // swaps its face in place — scroll position and snap state never change.

  const priceFor = (n) => {
    const disc = selectDiscount(n, discountConfig);
    const full = n * framePrice;
    let total = Math.round(full * (1 - disc / 100));
    // 1–4 frame cards: a redemption order that small pays shipping (free at
    // 5+), so the card price bakes it in — the gift stays truly free for the
    // recipient. Mirrors the server's computeGiftPrice.
    if (n < 5) total += shippingPrice;
    // TEST-ONLY: mirrors the server's computeGiftPrice override (tiers 12 & 36
    // → ₪2) so the display matches what the drawer actually charges. Active
    // only on test/staging/local builds — the gate defaults to OFF, so an
    // unexpected build-arg can never leak ₪2 to production.
    if (/\/\/(test|staging)\.blends|localhost/.test(process.env.REACT_APP_BASE_URL || "") && (n === 12 || n === 36)) {
      total = 2;
    }
    return { total, full: Math.round(full), per: n > 0 ? Math.round((total / n) * 10) / 10 : 0, disc };
  };
  const { total, full: fullPrice, disc: discNow } = priceFor(frames);

  // Card slots only (excludes the end spacers).
  const slots = () => (carRef.current ? carRef.current.querySelectorAll(".gc-card-slot") : []);

  // User scroll → live nearest-card highlight. Swipe settling itself belongs
  // to NATIVE scroll-snap (snap-stop: always = one-card paging). Our own
  // glide/FLIP scrolls are ignored (the highlight is already set, and a
  // programmatic glide passing 3 cards must not shuffle it through each one).
  const onCarScroll = () => {
    if (glideInFlight()) return;
    if (tickRef.current) return;
    tickRef.current = true;
    requestAnimationFrame(() => {
      tickRef.current = false;
      if (glideInFlight()) return;
      setSelectedIdx(nearestIdx());
      setIsCustom(false);
    });
  };
  // A touch takes over: the glide window closes and native snap owns the
  // gesture (it never stopped being armed).
  const onCarTouchStart = () => {
    touchOnRef.current = true;
    cancelGlideWindow();
  };
  const onCarTouchEnd = () => { touchOnRef.current = false; };

  const pendingGlideRef = useRef(null);
  // Custom exit is SEQUENCED (owner): first the tapped tier arrives alone —
  // the old card cross-fades out, the target fades in while gliding to
  // centre — and only then the rest of the carousel fades back in.
  const [customExitPhase, setCustomExitPhase] = useState(null); // 'hold' | 'reveal' | null
  const customPhaseTimers = useRef([]);
  const clearCustomPhase = () => {
    customPhaseTimers.current.forEach(clearTimeout);
    customPhaseTimers.current = [];
  };
  useEffect(() => () => clearCustomPhase(), []);
  const selectCard = (i) => {
    logGiftFlowEvent("tier_selected", { frames: BUNDLES[i], fromCustom: isCustom });
    setSelectedIdx(i);
    if (isCustom) {
      // glide AFTER the re-render, measured against the final geometry
      pendingGlideRef.current = i;
      setIsCustom(false);
      clearCustomPhase();
      setCustomExitPhase("hold");
      customPhaseTimers.current = [
        setTimeout(() => setCustomExitPhase("reveal"), 550),
        setTimeout(() => setCustomExitPhase(null), 1100),
      ];
      return;
    }
    glideTo(i);
  };
  useLayoutEffect(() => {
    if (isCustom || pendingGlideRef.current == null) return;
    const i = pendingGlideRef.current;
    pendingGlideRef.current = null;
    glideTo(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustom]);

  // Validation — same rules as the onboarding form (identical phone regex).
  // Name + phone are required; email only has to be valid when filled.
  const PHONE_RE = /^0[2-9]\d{7,8}$/;
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const giftErrors = () => {
    const errs = {};
    if (!senderName.trim()) errs.senderName = "חסר שם הנותן";
    if (!buyerPhone.trim()) errs.phoneNumber = "חסר הטלפון שלך לתשלום";
    else if (!PHONE_RE.test(buyerPhone.trim())) errs.phoneNumber = "מספר הטלפון לא תקין";
    if (buyerEmail.trim() && !EMAIL_RE.test(buyerEmail.trim())) errs.email = "האימייל לא תקין";
    if (withRecipient) {
      if (!recipientPhone.trim()) errs.recipientPhone = "חסר טלפון של המקבל/ת";
      else if (!PHONE_RE.test(recipientPhone.trim())) errs.recipientPhone = "טלפון המקבל/ת לא תקין";
      if (recipientEmail.trim() && !EMAIL_RE.test(recipientEmail.trim())) errs.recipientEmail = "אימייל המקבל/ת לא תקין";
    }
    return errs;
  };
  const errsNow = giftErrors();
  const giverOk = !errsNow.senderName && !errsNow.phoneNumber && !errsNow.email;
  const allOk = giverOk && !errsNow.recipientPhone && !errsNow.recipientEmail;

  const clearFieldError = (k) =>
    setFieldErrors((f) => (f[k] ? { ...f, [k]: undefined } : f));

  const validateDetails = () => {
    const errs = giftErrors();
    setFieldErrors(errs);
    const first = errs.senderName || errs.phoneNumber || errs.email || errs.recipientPhone || errs.recipientEmail;
    setError(first || "");
    return !first;
  };

  /* ── extras modal (greeting / recipient) handlers ── */
  const greetingSet = withGreeting && !!greeting.trim();
  const recipientSet = withRecipient && (!!recipientPhone.trim() || !!recipientName.trim());

  const openGreetingModal = () => { setWithGreeting(true); setExtrasModal("greeting"); };
  const openRecipientModal = () => { setWithRecipient(true); setExtrasModal("recipient"); };

  // X / overlay tap: keep what was typed; an empty editor reads as "never mind"
  const closeExtrasModal = () => {
    if (extrasModal === "greeting" && !greeting.trim()) setWithGreeting(false);
    if (extrasModal === "recipient" && !recipientPhone.trim() && !recipientName.trim() && !recipientEmail.trim()) setWithRecipient(false);
    setExtrasModal(null);
  };

  const saveExtras = () => {
    if (extrasModal === "recipient") {
      if (!recipientPhone.trim() && !recipientName.trim() && !recipientEmail.trim()) { setWithRecipient(false); setExtrasModal(null); return; }
      const bad = !recipientPhone.trim()
        ? "חסר טלפון של המקבל/ת"
        : !PHONE_RE.test(recipientPhone.trim()) ? "טלפון המקבל/ת לא תקין" : "";
      if (bad) { setFieldErrors((f) => ({ ...f, recipientPhone: bad })); return; }
      if (recipientEmail.trim() && !EMAIL_RE.test(recipientEmail.trim())) {
        setFieldErrors((f) => ({ ...f, recipientEmail: "אימייל המקבל/ת לא תקין" })); return;
      }
    }
    closeExtrasModal();
  };

  // removal happens on the pill itself (✕), not inside the modal
  const removeGreeting = () => { setGreeting(""); setWithGreeting(false); };
  const removeRecipient = () => {
    setWithRecipient(false); setRecipientName(""); setRecipientPhone(""); setRecipientEmail("");
    setSendWhen("now"); setSendDate("");
    clearFieldError("recipientPhone"); clearFieldError("recipientEmail");
  };

  const currentDetails = () => ({
    recipientName: withRecipient ? recipientName : "",
    recipientPhone: withRecipient ? recipientPhone : "",
    recipientEmail: withRecipient ? recipientEmail : "",
    senderName,
    greeting: withGreeting ? greeting : "",
    purchaserPhone: buyerPhone,
    purchaserEmail: buyerEmail,
    // datetime-local is timezone-less — convert via the USER's clock to ISO so
    // the server stores the exact intended moment (no past: input min = now,
    // and anything already-due just sends immediately at activation)
    scheduledSendAt:
      withRecipient && sendWhen === "later" && sendDate
        ? new Date(sendDate).toISOString()
        : null,
    // CRO split-test attribution: which total-placement arm the buyer saw.
    // Mobile is outside the experiment — no arm, so it never skews the stats.
    visitorId: getVisitorId(),
    abTotalPlacement: isMobile ? null : totalVariant,
  });

  // Create (or refresh) the PENDING intent. Reused across retries and method
  // switches while the frame count is unchanged; a tier change = new intent
  // (the amount is priced server-side at creation).
  // One create/refresh in flight at a time: the Apple Pay pre-arm below re-runs
  // on every keystroke, and a second run while the first create was still in
  // flight produced two PENDING intents 0.6 s apart (2026-09-17). Callers get
  // the same promise; the next call refreshes the details on the kept intent.
  const intentInFlightRef = useRef(null);
  const ensureIntent = () => {
    if (intentInFlightRef.current) return intentInFlightRef.current;
    const run = (async () => {
      const details = currentDetails();
      if (intentRef.current && intentRef.current.frames === frames) {
        try {
          await updateGiftCardIntent({ giftOrderId: intentRef.current.giftOrderId, ...details });
          return intentRef.current;
        } catch (e) { /* stale/paid intent — fall through to a fresh one */ }
      }
      const intent = await createGiftCardIntent({ frames, ...details });
      if (!intent.success) throw new Error(intent.error || "שגיאה");
      intentRef.current = { giftOrderId: intent.giftOrderId, amount: intent.amount, frames };
      return intentRef.current;
    })();
    intentInFlightRef.current = run;
    return run.finally(() => { if (intentInFlightRef.current === run) intentInFlightRef.current = null; });
  };

  // Socket + poll until the webhook activates the card, then go to gift-success.
  const beginWatch = (giftOrderId) => {
    localStorage.setItem("orderId", giftOrderId);
    initializeSocket();
    subscribeToOrderStatus(() => {});
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const s = await getGiftCardStatus(giftOrderId);
        if (s && s.success && s.status && s.status !== "PENDING") {
          clearInterval(pollRef.current);
          navigate(`/gift-success/${giftOrderId}`);
        }
      } catch (e) { /* keep waiting */ }
    }, 3000);
  };

  /* Shared payment prep: create/refresh the intent, flip the app into gift-
     checkout mode and start watching for the webhook activation. */
  const prepGiftIntent = async () => {
    const it = await ensureIntent();
    logGiftFlowEvent("checkout_opened", { frames: it.frames, amount: it.amount, method: "checkout" }, mode);
    localStorage.setItem("orderId", it.giftOrderId);
    setGiftCheckout({
      giftOrderId: it.giftOrderId,
      amount: it.amount,
      frames: it.frames,
      buyerName: senderName || recipientName || "",
      buyerPhone,
      buyerEmail,
    });
    beginWatch(it.giftOrderId);
    return it;
  };

  /* CTA (both widths) — open the payment-methods popup DIRECTLY, no checkout
     drawer: gift orders have no shipping/cart step, so the drawer only added
     noise. The GC- intent is created first, and the popup is fully gift-mode
     aware — Bit / Apple Pay / Google Pay / card all charge the server-priced
     amount against the GC- order (card runs one full J7 charge, no 1₪ leg). */
  const openGiftPaymentMethods = async () => {
    setError("");
    if (!validateDetails()) return;
    setPayBusy(true);
    try {
      await prepGiftIntent();
      const isMobileNow = window.matchMedia("(max-width: 767px)").matches;
      secondModal("open", isMobileNow ? "selectCardMobile" : "selectCard");
    } catch (e) {
      console.error("Gift payment methods open failed:", e);
      setError(e.message || "שגיאה, נסו שוב");
    } finally {
      setPayBusy(false);
    }
  };

  /* Mobile iOS — the footer CTA IS Apple Pay: same proven gift path the
     payment-methods modal uses (GC- order already exists → handshake →
     ApplePayMobile, which routes GC- success to /gift-success). */
  const openGiftApplePay = async () => {
    setError("");
    if (!validateDetails()) return;
    setPayBusy(true);
    try {
      const it = await prepGiftIntent();
      const hs = await getApplePayHandshake(it.amount);
      if (hs.status !== 200 || !hs.token) throw new Error("שגיאה בתשלום, נסו שוב");
      secondModal("open", "applePayMobile", {
        amount: it.amount,
        orderId: it.giftOrderId,
        handshakeToken: hs.token,
        onClose: () => setPayBusy(false),
      });
    } catch (e) {
      console.error("Gift Apple Pay open failed:", e);
      setError(e.message || "שגיאה, נסו שוב");
    } finally {
      setPayBusy(false);
    }
  };

  /* ── Direct Apple Pay (mobile iOS): the footer button opens the NATIVE
     sheet on its own tap, like the cart's checkout. hfChargeApple demands a
     synchronous user-gesture call, so everything async — the Tranzila
     script, the GC- intent and the handshake token — is preloaded while the
     user types (CheckoutMobile's preload pattern). If anything isn't ready
     at tap time the tap falls back to the ApplePayMobile modal path. ── */
  const [apArmed, setApArmed] = useState(false); // intent + token preloaded
  const apRef = useRef(null);                    // {orderId, amount, frames, token}
  const apSessionRef = useRef(false);            // a sheet is open (no double-fire)
  const apWatchRef = useRef(null);               // dismiss-watch cleanup

  // Sheet session resolved (paid / failed / dismissed) — release the button;
  // a used handshake token is dead, so re-arm with a fresh one for retries.
  const finishApSession = (ok, oid) => {
    if (!apSessionRef.current) return;
    apSessionRef.current = false;
    if (apWatchRef.current) { apWatchRef.current(); apWatchRef.current = null; }
    setPayBusy(false);
    if (ok) {
      localStorage.removeItem("orderId");
      setGiftCheckout(null);
      navigate(`/gift-success/${oid}`);
      return;
    }
    setApArmed(false);
    const cur = apRef.current;
    if (cur) {
      getApplePayHandshake(cur.amount)
        .then((hs) => {
          if (hs.status === 200 && hs.token) {
            apRef.current = { ...cur, token: hs.token };
            setApArmed(true);
          }
        })
        .catch(() => {});
    }
  };

  // Cancelling the sheet never fires the hfChargeApple callback. Primary
  // detection is the shared ApplePaySession.begin cancel patch (also used by
  // CheckoutMobile — same guard flag, patched once); focus/visibility return
  // plus a long watchdog cover the cases where the native event never comes.
  const patchApplePaySessionCancel = () => {
    try {
      const proto = window.ApplePaySession && window.ApplePaySession.prototype;
      if (!proto || proto.__blendsCancelPatched) return;
      const originalBegin = proto.begin;
      proto.begin = function patchedBegin() {
        try {
          this.addEventListener("cancel", () => {
            window.dispatchEvent(new CustomEvent("blends-applepay-cancel"));
          });
        } catch (e) { /* non-fatal — the watchdog covers us */ }
        return originalBegin.apply(this, arguments);
      };
      proto.__blendsCancelPatched = true;
    } catch (e) { /* non-fatal */ }
  };

  const armApDismissWatch = () => {
    const openedAt = Date.now();
    let grace = null;
    const onReturn = () => {
      if (!apSessionRef.current) return;
      if (document.visibilityState === "hidden") return; // the sheet opening
      if (Date.now() - openedAt < 400) return;           // ignore open churn
      if (grace) return;
      // let a real payment callback win first; otherwise treat as dismissed
      grace = setTimeout(() => { grace = null; finishApSession(false); }, 1200);
    };
    const dog = setTimeout(() => finishApSession(false), 150000);
    document.addEventListener("visibilitychange", onReturn);
    window.addEventListener("focus", onReturn);
    apWatchRef.current = () => {
      document.removeEventListener("visibilitychange", onReturn);
      window.removeEventListener("focus", onReturn);
      if (grace) clearTimeout(grace);
      clearTimeout(dog);
    };
  };

  useEffect(() => {
    if (!(isMobile && canApplePay)) return;
    patchApplePaySessionCancel();
    const onCancel = () => finishApSession(false);
    window.addEventListener("blends-applepay-cancel", onCancel);
    return () => window.removeEventListener("blends-applepay-cancel", onCancel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, canApplePay]);

  // Script warms up as soon as the pay screen shows; the intent + token arm
  // once the required fields pass, and re-arm (debounced) on every detail
  // change so the server-stored details are never stale at tap time.
  useEffect(() => {
    if (isMobile && canApplePay && step === 2) loadTranzilaApplePayScript().catch(() => {});
  }, [isMobile, canApplePay, step]);
  // owner telemetry: the moment the required giver fields first pass
  const loggedAllOkRef = useRef(false);
  useEffect(() => {
    if (step !== 2) { loggedAllOkRef.current = false; return; }
    if (allOk && !loggedAllOkRef.current) {
      loggedAllOkRef.current = true;
      logGiftFlowEvent("required_fields_completed", {
        frames,
        name: senderName,
        phone: buyerPhone,
        hasEmail: !!buyerEmail.trim(),
        withRecipient,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allOk, step]);

  useEffect(() => {
    if (!(isMobile && canApplePay && step === 2 && allOk)) { setApArmed(false); return; }
    let stale = false;
    const t = setTimeout(async () => {
      try {
        const it = await ensureIntent();
        const hs = await getApplePayHandshake(it.amount);
        if (stale) return;
        if (hs.status === 200 && hs.token) {
          apRef.current = { orderId: it.giftOrderId, amount: it.amount, frames: it.frames, token: hs.token };
          setApArmed(true);
        }
      } catch (e) { /* not armed — the tap falls back to the modal path */ }
    }, 350);
    return () => { stale = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, canApplePay, step, allOk, frames, senderName, buyerPhone, buyerEmail,
    withRecipient, recipientName, recipientPhone, recipientEmail, withGreeting, greeting, sendWhen, sendDate]);

  /* The footer button's tap — MUST stay synchronous up to hfChargeApple. */
  const launchGiftApplePay = () => {
    if (payBusy || apSessionRef.current) return;
    const ap = apArmed ? apRef.current : null;
    if (!ap || typeof window.hfChargeApple !== "function" || !validateDetails()) {
      // preload not ready (or fields invalid) — the proven modal path handles it
      openGiftApplePay();
      return;
    }
    setError("");
    // fire-and-forget details sync: a tap inside the re-arm debounce window
    // would otherwise pay against pre-edit details (the webhook activates the
    // card seconds later — this lands first)
    updateGiftCardIntent({ giftOrderId: ap.orderId, ...currentDetails() }).catch(() => {});
    // the same prep the modal path does, just synchronous so the sheet still
    // opens from this very tap
    logGiftFlowEvent("checkout_opened", { frames: ap.frames, amount: ap.amount, method: "apple_pay_direct" }, mode);
    localStorage.setItem("orderId", ap.orderId);
    setGiftCheckout({
      giftOrderId: ap.orderId,
      amount: ap.amount,
      frames: ap.frames,
      buyerName: senderName || recipientName || "",
      buyerPhone,
      buyerEmail,
    });
    beginWatch(ap.orderId);
    apSessionRef.current = true;
    setPayBusy(true);
    armApDismissWatch();
    try {
      window.hfChargeApple({
        labelApple: "גיפט קארד בלנדס",
        terminal_name: "blends",
        currency_code: "ILS",
        amount: ap.amount,
        tran_mode: "A",
        thtk: ap.token,
        response_language: "english",
        supported_network: ["visa", "masterCard", "amex", "discover"],
        pdesc: `גיפט קארד בלנדס ×${ap.frames}`,
        contact: senderName || "",
        email: buyerEmail || "",
        phone: buyerPhone || "",
        uniqueid: ap.orderId,
        notify_url: `${window.location.origin}/api/apple-pay-webhook?orderId=${ap.orderId}`,
      }, (err, response) => {
        const ok = !err &&
          ((response && response.transaction_response && response.transaction_response.success === true) ||
            (response && response.status === "ok"));
        finishApSession(ok, ap.orderId);
      });
    } catch (e) {
      console.error("Gift Apple Pay launch failed:", e);
      finishApSession(false);
    }
  };

  // "אעדיף לבחור תמונות" — modal: resume the normal gift flow; page: go upload.
  const handleSelfAdd = () => {
    if (onSelfAdd) return onSelfAdd();
    navigate("/upload-your-image");
  };

  const closeBtn = mode === "modal" && onClose ? (
    <button className="gc-close" onClick={onClose} aria-label="סגירה">×</button>
  ) : null;
  const selfAdd = mode === "modal" ? (
    <div className="gc-selfadd">
      מעדיפ/ה לבחור תמונות בעצמך?{" "}
      <button type="button" onClick={() => onSelfAdd && onSelfAdd()}>אני אוסיף תמונות בעצמי →</button>
    </div>
  ) : null;

  /* ── Paying ── */
  if (phase === "paying") {
    return (
      <div className="gc-flow" dir="rtl">
        {closeBtn}
        <div className="gc-scroll gc-paying">
          <div className="gc-spinner" />
          <h2>ממתינים לתשלום…</h2>
          <p>
            מאשרים את התשלום…
            <br />לאחר התשלום המתנה תישלח אוטומטית ב‑SMS.
          </p>
        </div>
      </div>
    );
  }

  /* ── Step 0: opening / intro (tilted card + V5 "דמיינו את הרגע" + colourful rows) ── */
  if (step === 0) {
    return (
      <div className="gc-flow gc-flow-intro" dir="rtl">
        {closeBtn}
        <div className="gc-scroll gc-intro" key="step0">
          <div className="gc-intro-cardcol">
            <div className="gc-intro-card"><img className="gc-intro-cardimg" src={`${ASSET}/card-green.webp`} alt="כרטיס מתנה בלנדס" /></div>
          </div>

          <div className="gc-intro-headwrap">
            <p className="gc-intro-sub">כרטיס מתנה שנשאר על הקיר - הם בוחרים את התמונות האהובות, ואתם נותנים את הרגש.</p>
            <h2 className="gc-intro-h">דמיינו את הרגע…</h2>
          </div>

          <div className="gc-intro-rows">
            <div className="gc-irow r1">
              <span className="gc-irow-ic">
                <svg viewBox="0 0 24 24"><path transform="translate(1.6 7.6) scale(0.375)" d="M55.62,0c-2.24,13.33-13.84,23.49-27.81,23.49S2.24,13.33,0,0h10.28c1.62,8.21,8.85,14.39,17.53,14.39s15.91-6.18,17.53-14.39h10.28Z" /></svg>
              </span>
              <div className="gc-irow-t"><b>החיוך כשהמתנה נתלית על הקיר</b><span>ובכל מבט בקיר - הם נזכרים בכם</span></div>
            </div>
            <div className="gc-irow r2">
              <span className="gc-irow-ic">
                <svg viewBox="0 0 24 24" fillRule="evenodd"><path d="M7.7 3.5h8.6a4.2 4.2 0 0 1 4.2 4.2v8.6a4.2 4.2 0 0 1-4.2 4.2H7.7a4.2 4.2 0 0 1-4.2-4.2V7.7a4.2 4.2 0 0 1 4.2-4.2ZM6.7 12.65l3.65 3.65 7.05-7.05-1.7-1.7-5.35 5.35-1.95-1.95-1.7 1.7Z" /></svg>
              </span>
              <div className="gc-irow-t"><b>הם בוחרים, אתם מקבלים את הקרדיט</b><span>בדיוק התמונות שהם אוהבים - מהטלפון ישר לקיר</span></div>
            </div>
            <div className="gc-irow r3">
              <span className="gc-irow-ic">
                <svg viewBox="0 0 24 24" fillRule="evenodd"><path d="M21 11.5a8.5 8.5 0 0 1-8.5 8.5c-1.55 0-3-.41-4.26-1.13L3.5 20l1.18-4.5A8.5 8.5 0 1 1 21 11.5ZM12.5 15.3c2.52-2.22 3.84-3.42 3.84-4.86 0-1.14-.9-1.98-2.04-1.98-.72 0-1.36.35-1.8.92-.44-.58-1.08-.92-1.8-.92-1.14 0-2.04.84-2.04 1.98 0 1.44 1.32 2.64 3.84 4.86Z" /></svg>
              </span>
              <div className="gc-irow-t"><b>מגיע אליהם ב‑SMS תוך דקות</b><span>בוחרים כמות תמונות, מוסיפים ברכה אישית - ומפתיעים</span></div>
            </div>
          </div>
        </div>

        <div className="gc-foot gc-foot-intro">
          <button type="button" className="gc-cta" onClick={() => setStep(1)}>מגניב, בואו נבחר גיפט קארד</button>
          <button type="button" className="gc-intro-alt" onClick={handleSelfAdd}>אעדיף לבחור תמונות בעצמי</button>
        </div>
      </div>
    );
  }

  /* ── Steps 1 + 2, merged on EVERY width: the video + card stay put, and
     only the tier pane + CTA morph into the "cart" area with motion.
     Both widths show the inline giver form; on mobile the pinned card
     shrinks a step (flex-basis morph) so it all fits one viewport. ── */
  if (step === 1 || step === 2) {
    const payMode = step === 2;
    return (
      <div className={`gc-flow gc-flow-choose${payMode ? " gc-flow-pay" : ""}${payMode && motionSettled ? " gc-settled" : ""}`} dir="rtl">
        {closeBtn}
        <div className="gc-scroll" key="step1">
          {/* the tier clip leads the screen — flush under the top bar on mobile, wide hero strip on desktop.
              In pay mode the floating back button anchors to this pane, so it
              tracks the strip's height on every viewport (mobile: docked
              right, just below the clip; desktop: over the hero corner). */}
          <div className="gc-pane-video">
            <GiftEnvVideo frames={frames} />
            {payMode && (
              <button type="button" className="gc-back gc-back-float" onClick={() => goStep(1)} aria-label="חזרה">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
              </button>
            )}
          </div>
          <div className="gc-panes">
          <div className="gc-pane-card">
          {/* pay mode (desktop, A/B arm 'card'): the total crowns the card —
              price in brand green */}
          {payMode && !isMobile && totalVariant === "card" && (
            <div className="gc-card-total">
              <span className="lbl">סה״כ</span>
              <span className="amt" style={{ direction: "ltr" }}>
                {discNow > 0 && <span className="was">₪{fullPrice}</span>}
                <span className="now">₪{total}</span>
              </span>
            </div>
          )}
          <div className={`gc-carousel${isCustom || customExitPhase === "hold" ? " gc-car-custom" : ""}${!isCustom && customExitPhase === "reveal" ? " gc-car-reveal" : ""}`} ref={carRef}
            onScroll={isCustom || payMode ? undefined : onCarScroll}
            onTouchStart={onCarTouchStart} onTouchEnd={onCarTouchEnd} onTouchCancel={onCarTouchEnd}
            onWheel={cancelGlideWindow}>
            <span className="gc-spacer" aria-hidden="true" />
            {/* ALL six slots stay mounted in EVERY mode — the scroller's
                structure never changes, so snap offsets and paint never go
                stale. Custom mode just fades the neighbours (like pay) and
                the SELECTED card wears the custom face in place: keyed by
                THEME so the blur-switch fires only when the amount crosses
                a tier range, not on every stepper tick. */}
            {BUNDLES.map((n, i) => {
              const isSel = selectedIdx === i;
              const showCustom = isCustom && isSel;
              const shownFrames = showCustom ? customFrames : n;
              const shownTheme = themeFor(shownFrames);
              // blur-switch ONLY when the custom amount crosses into a
              // DIFFERENT theme range. Entering custom on the native theme
              // keeps the very same element (stable theme key) — the card
              // does not re-render, move or resize by a single pixel.
              const pop = showCustom && shownTheme !== themeFor(n);
              return (
                <button
                  key={n}
                  type="button"
                  className={`gc-card-slot${isSel ? " on" : ""}${pop ? " gc-slot-custom" : ""}`}
                  onClick={() => selectCard(i)}
                >
                  <GiftCardVisual key={shownTheme} frames={shownFrames} />
                </button>
              );
            })}
            <span className="gc-spacer" aria-hidden="true" />
          </div>
          {/* mobile: the question pins with the card — its copy morphs with
              the step (keyed span re-runs the rise-in on each swap) */}
          <h2 className="gc-heading gc-heading-sticky">
            <span className="gc-heading-swap" key={payMode ? "pay" : "choose"}>
              {payMode ? <><b>מי נותן.</b> <span>ומי מקבל</span></> : <><b>מתנה.</b> <span>איזה כרטיס מתאים לך?</span></>}
            </span>
          </h2>
          {/* pay mode (desktop): two reassurance features tucked under the card */}
          {payMode && (
            <div className="gc-features">
              <div className="gc-feature">{I.bubble}נשלח מיד ב‑SMS</div>
              <div className="gc-feature">{I.truck}מגיע ממוסגר עד הבית</div>
            </div>
          )}
          </div>

          <div className="gc-pane-main">
          {payMode ? (
            /* the "cart" pane — keyed so the swap re-runs the rise-in motion */
            <div className="gc-pane-swap" key="details">
              {isMobile ? (
                <>
                  {/* the desktop giver form, inline: labelled Input2 fields
                      with the required marks — no sheet detour */}
                  {renderGiverForm()}
                  {renderExtras()}
                </>
              ) : (
                <>
                  <h2 className="gc-heading"><b>מי הנותן.</b> <span>ומי מקבל</span></h2>
                  {renderGiverForm()}
                  {renderExtras()}
                  {/* A/B arm 'cta': totals anchor to the pane bottom — directly
                      above the CTA, which itself never moves */}
                  {totalVariant === "cta" && (
                    <div className="gc-foot-total">
                      <span className="lbl">סה״כ</span>
                      <span className="amt" style={{ direction: "ltr" }}>
                        {discNow > 0 && <span className="was">₪{fullPrice}</span>}
                        <span className={`now${discNow > 0 ? " deal" : ""}`}>₪{total}</span>
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
          <div className="gc-pane-swap" key="choose">
          <h2 className="gc-heading gc-heading-main"><b>מתנה.</b> <span>איזה כרטיס מתאים לך?</span></h2>
          <div className="gc-bundles">
            {BUNDLES.map((n, i) => {
              const { total: t, full, per, disc } = priceFor(n);
              const save = full - t;
              const gold = BUNDLE_LABELS[n] === "משתלם";
              return (
                <button
                  key={n}
                  type="button"
                  className={`gc-bundle${!isCustom && selectedIdx === i ? " on" : ""}`}
                  onClick={() => selectCard(i)}
                >
                  <div className="gc-bundle-meta">
                    <div className="title">{n} בלנדס{BUNDLE_LABELS[n] && <span className={`gc-bundle-badge${gold ? " gold" : ""}`}>{BUNDLE_LABELS[n]}</span>}</div>
                    <div className="ship">{per} ₪ ליחידה<span className="ship-note"> · שליח עד הבית</span></div>
                  </div>
                  <div className="gc-bundle-price">
                    <div className="gc-price-row">
                      <span className={`total${disc > 0 ? " deal" : ""}`}>₪{t}</span>
                      {disc > 0 && <span className="was">₪{full}</span>}
                    </div>
                    {save > 0 && <span className="gc-save">חסכו ₪{save}</span>}
                  </div>
                </button>
              );
            })}
          </div>

          {!isCustom ? (
            <button type="button" className="gc-custom-link" onClick={() => { clearCustomPhase(); setCustomExitPhase(null); setCustomFrames(BUNDLES[selectedIdx]); setIsCustom(true); logGiftFlowEvent("custom_opened", { from: BUNDLES[selectedIdx] }); }}>
              + או בחרו את כמות התמונות המותאמת אישית עבורכם
            </button>
          ) : (
            <div className="gc-custom-row">
              <label>כמות מותאמת אישית:</label>
              <div className="gc-stepper">
                <button type="button" onClick={() => stepCustom(-1)} disabled={customFrames <= 1}>−</button>
                <input type="number" min="1" max="50" value={customFrames}
                  onChange={(e) => setCustomFrames(Math.max(1, Math.min(50, parseInt(e.target.value, 10) || 1)))} />
                <button type="button" onClick={() => stepCustom(1)} disabled={customFrames >= 50}>+</button>
              </div>
              <span className="per" style={{ direction: "ltr" }}>
                {priceFor(customFrames).disc > 0 && <span className="was">₪{priceFor(customFrames).full}</span>} ₪{priceFor(customFrames).total}
              </span>
            </div>
          )}
          </div>
          )}
          </div>
          </div>
        </div>

        <div className="gc-foot">
          {payMode && error && <div className="gc-error">{error}</div>}
          {/* mobile pay: the total sits in the foot, right above the CTA */}
          {payMode && isMobile && (
            <div className="gc-foot-total">
              <span className="lbl">סה״כ</span>
              <span className="amt" style={{ direction: "ltr" }}>
                {discNow > 0 && <span className="was">₪{fullPrice}</span>}
                <span className={`now${discNow > 0 ? " deal" : ""}`}>₪{total}</span>
              </span>
            </div>
          )}
          {payMode ? (
            isMobile ? (
              /* desktop gating, cart colours: the CTA stays greyed until the
                 required giver fields pass — then it IS the payment (Apple
                 Pay on iOS, methods elsewhere) */
              allOk && canApplePay ? (
                <button type="button" className="gc-apple-btn" onClick={launchGiftApplePay}
                  disabled={payBusy} aria-label="Apple Pay" />
              ) : (
                <button type="button" className="gc-cta gc-cta-dark" onClick={openGiftPaymentMethods}
                  disabled={payBusy || !allOk}>
                  {payBusy ? "רגע…" : "בחירת אמצעי תשלום"}
                </button>
              )
            ) : (
              <button type="button" className="gc-cta" onClick={openGiftPaymentMethods} disabled={payBusy || !allOk}>
                {payBusy ? "רגע…" : "אישור ותשלום"}
              </button>
            )
          ) : (
            <button type="button" className="gc-cta" onClick={() => goStep(2)}>בחרתי, אפשר להמשיך</button>
          )}
          {/* the review-images-style reassurance line, right under the CTA —
              choose screen rotates the approved benefit lines */}
          {payMode
            ? <div className="gc-note">תשלום מאובטח · הגיפט קארד נשלח מיד ב‑SMS</div>
            : <GcNoteTicker lines={[
                <>{I.bubble} הגיפט קארד נשלח מיד ב‑SMS</>,
                <>{I.frame} התמונות מגיעות ארוזות וממוסגרות</>,
                <>{I.truck} כולל משלוח - שליח עד הבית</>,
                <>{I.heart} הם בוחרים את התמונות - אתם מקבלים את הקרדיט</>,
              ]} />}
          {!payMode && <GiftBenefitsGrid />}
          {selfAdd}
          <div className="gc-progress"><span style={{ width: payMode ? "100%" : "50%" }} /></div>
        </div>

        {/* greeting / recipient editor modal (mobile: sheet, desktop: dialog) */}
        {renderExtrasModal()}
      </div>
    );
  }

  /* The giver contact form — inline on BOTH widths (desktop pane + the mobile
     pay screen): only what payment requires, like the cart's contact modal.
     Name + phone are required (asterisk), email is optional.
     Hoisted function declarations: the merged choose/details branch above
     this point renders them too. */
  function renderGiverForm() {
    return (
    <div className="gc-form">
      <div className="form__row">
        <Input name="senderName" label="מאת" placeholder="מאת (למשל: אבא ואמא)"
          value={senderName}
          onChange={(e) => { setSenderName(e.target.value); clearFieldError("senderName"); }}
          isRequired={!senderName.trim()} error={fieldErrors.senderName} icon={I.user} />
      </div>
      <div className="form__row">
        <Input name="phoneNumber" label="הטלפון שלך" placeholder="הטלפון שלך"
          value={buyerPhone}
          onChange={(e) => { setBuyerPhone(e.target.value); clearFieldError("phoneNumber"); }}
          isRequired={!buyerPhone.trim()} error={fieldErrors.phoneNumber} maxLength={10} icon={I.tel} />
      </div>
      <div className="form__row">
        <Input name="email" label="האימייל שלך" placeholder="האימייל שלך (הכרטיס גם למייל)"
          value={buyerEmail}
          onChange={(e) => { setBuyerEmail(e.target.value); clearFieldError("email"); }}
          error={fieldErrors.email} icon={I.mail} />
      </div>
    </div>
    );
  }

  /* Greeting + recipient add-ons — a warm invitation line + pill triggers
     (F direction). The editors open in a MODAL (mobile: bottom sheet,
     desktop: centered dialog); a filled add-on flips its pill to the V-mark
     done state. Shared by the desktop pane and the mobile details screen. */
  function renderExtras() {
    return (
    <div className="gc-extras">
      <div className="gc-extras-label">רוצים לפנק עוד קצת?</div>
      <div className="gc-extras-pills">
        <button type="button" className={`gc-pill${greetingSet ? " on" : ""}`} onClick={openGreetingModal}>
          {greetingSet ? (
            <>
              {VTick} ברכה אישית
              <span className="gc-pill-x" role="button" aria-label="הסרת הברכה"
                onClick={(e) => { e.stopPropagation(); removeGreeting(); }}>×</span>
            </>
          ) : "＋ ברכה אישית"}
        </button>
        <button type="button" className={`gc-pill${recipientSet ? " on" : ""}`} onClick={openRecipientModal}>
          {recipientSet ? (
            <>
              {VTick} {recipientName ? `ל${recipientName}` : "גם למקבל"}
              <span className="gc-pill-x" role="button" aria-label="הסרת המקבל"
                onClick={(e) => { e.stopPropagation(); removeRecipient(); }}>×</span>
            </>
          ) : "＋ שלח גם למקבל"}
        </button>
      </div>
    </div>
    );
  }

  /* The greeting / recipient editor modal. */
  function renderExtrasModal() {
    if (!extrasModal) return null;
    const isGreeting = extrasModal === "greeting";
    return (
      <div className="gc-sheet-overlay" onClick={closeExtrasModal}>
        <div className="gc-sheet gc-sheet-extras" onClick={(e) => e.stopPropagation()}>
          <div className="gc-sheet-head">
            <b>{isGreeting ? "ברכה אישית" : "פרטי המקבל/ת"}</b>
            <button type="button" className="gc-sheet-close" onClick={closeExtrasModal} aria-label="סגירה">×</button>
          </div>
          <div className="gc-sheet-body">
            {isGreeting ? (
              <>
                <div className="gc-field full" style={{ marginBottom: 0 }}>
                  <textarea value={greeting} maxLength={120} onChange={(e) => setGreeting(e.target.value)} placeholder="כמה מילים מהלב…" />
                </div>
                <div className="gc-greet-pretext">או בחרו, ברכה לפי מאורע:</div>
                <div className="gc-greet-pills">
                  {GREETING_PRESETS.map((g) => (
                    <button
                      key={g.label}
                      type="button"
                      className={`gc-greet-pill${greeting === g.text ? " on" : ""}`}
                      onClick={() => setGreeting(g.text)}
                    >
                      {g.label}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="gc-form">
                  <div className="form__row">
                    <Input name="recipientName" label="שם המקבל/ת" placeholder="שם המקבל/ת (למשל: נועה)"
                      value={recipientName} onChange={(e) => setRecipientName(e.target.value)} icon={I.user} />
                  </div>
                  <div className="form__row">
                    <Input name="recipientPhone" type="tel" inputMode="numeric" label="טלפון המקבל/ת" placeholder="טלפון המקבל/ת"
                      value={recipientPhone}
                      onChange={(e) => { setRecipientPhone(e.target.value); clearFieldError("recipientPhone"); }}
                      isRequired={withRecipient && !recipientPhone.trim()} error={fieldErrors.recipientPhone}
                      maxLength={10} icon={I.tel} />
                  </div>
                  <div className="form__row">
                    <Input name="recipientEmail" type="email" label="אימייל המקבל/ת (לא חובה)" placeholder="אימייל המקבל/ת (לא חובה)"
                      value={recipientEmail}
                      onChange={(e) => { setRecipientEmail(e.target.value); clearFieldError("recipientEmail"); }}
                      error={fieldErrors.recipientEmail} icon={I.mail} />
                  </div>
                </div>
                <div className="gc-seg">
                  <button type="button" className={sendWhen === "now" ? "on" : ""} onClick={() => setSendWhen("now")}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2L3 14h7l-1 8 10-12h-7z" /></svg>
                    שליחה מיידית
                  </button>
                  <button type="button" className={sendWhen === "later" ? "on" : ""} onClick={() => setSendWhen("later")}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M3 9h18M8 2v4M16 2v4" /></svg>
                    בתאריך עתידי
                  </button>
                </div>
                {sendWhen === "later" && (
                  <div className="gc-field" style={{ marginTop: "0.75rem" }}>
                    <label>תאריך ושעת משלוח</label>
                    {/* native OS date+time sheet; min = now, so no past dates */}
                    <input
                      type="datetime-local"
                      value={sendDate}
                      min={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
                      onChange={(e) => setSendDate(e.target.value)}
                      onClick={(e) => { try { e.target.showPicker && e.target.showPicker(); } catch (err) { /* native fallback */ } }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
          <div className="gc-sheet-foot">
            <button type="button" className="gc-cta" onClick={saveExtras}
              disabled={!isGreeting && !PHONE_RE.test(recipientPhone.trim())}>אישור</button>
          </div>
        </div>
      </div>
    );
  }

  // steps 0/1/2 and the paying phase all return above
  return null;
};

export default GiftCard;
